import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import ExcelJS from "exceljs";
import {
  fetchEmailLogs,
  clearAllEmailLogs,
  EmailLogEntry,
  EmailLogsFilters,
  fetchUnfinishedSubmissions,
  UnfinishedSubmissionRow
} from "../../api/emailLogsApi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "react-toastify";
import AdminLiveLogsPanel from "./AdminLiveLogsPanel";

const formatDate = (value?: string) => {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return date.toLocaleString();
};

const stageLabel = (row: UnfinishedSubmissionRow) => {
  if (row.legacy) {
    return "—";
  }
  const s = row.stage || "";
  if (s === "form_data") {
    return "Form data";
  }
  if (s === "email_unverified") {
    return "Email unverified";
  }
  return s || "—";
};

const unfinishedPrimaryLabel = (row: UnfinishedSubmissionRow) => {
  if (row.legacy) {
    return row.subject || row.preview || row.uid;
  }
  return row.publicToken || row.formUid || row.uid;
};

const unfinishedDate = (row: UnfinishedSubmissionRow) => {
  return row.updatedAt || row.sortDate || row.createdAt;
};

const statusBadgeClass = (status?: string) => {
  switch ((status || "").toLowerCase()) {
    case "sent":
      return "bg-green-100 text-green-700 border-green-200";
    case "failed":
      return "bg-red-100 text-red-700 border-red-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

/** After newlines, splits segments like `a: b c: d` so each `key: value` (colon + space) starts its own line. */
const expandDetailLines = (text: string): string[] => {
  const result: string[] = [];
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.replace(/\s+$/, "");
    if (line.trim().length === 0) {
      result.push("");
      continue;
    }
    const segments = line.trim().split(/\s+(?=[^\s:]+:\s)/);
    const trimmed = segments.map((segment) => segment.trim());
    if (trimmed.length > 1) {
      result.push(...trimmed);
    } else {
      result.push(line);
    }
  }
  return result;
};

const generalLogDetailText = (log: EmailLogEntry): string => {
  const detailText =
    log.status === "failed" ? log.errorMessage || "Failed" : log.preview || "—";
  if (typeof detailText === "string") {
    return expandDetailLines(detailText).join("\n");
  }
  return String(detailText);
};

const unfinishedExportDataText = (row: UnfinishedSubmissionRow): string => {
  if (row.legacy) {
    return expandDetailLines(row.preview || row.subject || "—").join("\n");
  }
  if (row.data === null || row.data === undefined) {
    return "—";
  }
  try {
    return JSON.stringify(row.data, null, 2);
  } catch {
    return String(row.data);
  }
};

const exportFilenameStamp = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
};

const downloadExcelWorkbook = async (workbook: ExcelJS.Workbook, baseName: string) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${baseName}-${exportFilenameStamp()}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
};

const exportEmailLogsToExcel = async (logs: EmailLogEntry[]) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("General logs", {
    views: [{ state: "frozen", ySplit: 1 }]
  });
  sheet.addRow([
    "Time",
    "Recipient",
    "Subject",
    "Status",
    "Service",
    "Category",
    "Message ID",
    "Details"
  ]);
  sheet.getRow(1).font = { bold: true };
  for (const log of logs) {
    sheet.addRow([
      log.createdAt ? formatDate(log.createdAt) : "—",
      log.to || (Array.isArray(log.toList) ? log.toList.join(", ") : "") || "—",
      log.subject ?? "—",
      log.status ?? "—",
      log.service ?? "—",
      log.category ?? "—",
      log.messageId ?? "—",
      generalLogDetailText(log)
    ]);
  }
  sheet.columns = [
    { width: 22 },
    { width: 28 },
    { width: 36 },
    { width: 12 },
    { width: 10 },
    { width: 18 },
    { width: 28 },
    { width: 48 }
  ];
  await downloadExcelWorkbook(workbook, "email-logs");
};

const exportUnfinishedSubmissionsToExcel = async (items: UnfinishedSubmissionRow[]) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Unfinished submissions", {
    views: [{ state: "frozen", ySplit: 1 }]
  });
  sheet.addRow([
    "Legacy",
    "Stage",
    "Updated",
    "Form / subject",
    "Email",
    "UID",
    "Public token",
    "Form UID",
    "Data"
  ]);
  sheet.getRow(1).font = { bold: true };
  for (const row of items) {
    sheet.addRow([
      row.legacy ? "Yes" : "No",
      stageLabel(row),
      formatDate(unfinishedDate(row) || undefined),
      unfinishedPrimaryLabel(row),
      row.email || row.to || "—",
      row.uid,
      row.publicToken ?? "—",
      row.formUid ?? "—",
      unfinishedExportDataText(row)
    ]);
  }
  sheet.columns = [
    { width: 10 },
    { width: 18 },
    { width: 22 },
    { width: 28 },
    { width: 28 },
    { width: 28 },
    { width: 20 },
    { width: 20 },
    { width: 56 }
  ];
  await downloadExcelWorkbook(workbook, "unfinished-submissions");
};

const renderUnfinishedDetailValue = (value: unknown): ReactNode => {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "object") {
    return (
      <pre className="mt-1 max-h-48 overflow-y-auto rounded border bg-background p-2 text-[11px] leading-snug whitespace-pre-wrap break-words">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  if (typeof value === "string") {
    const lines = expandDetailLines(value);
    if (lines.length <= 1) {
      return <span className="break-words">{lines[0] ?? value}</span>;
    }
    return (
      <div className="space-y-1">
        {lines.map((line, index) => (
          <div key={index} className="block break-words">
            {line.length > 0 ? line : "\u00a0"}
          </div>
        ))}
      </div>
    );
  }
  return <span className="break-words">{String(value)}</span>;
};

const renderUnfinishedDetailsBody = (row: UnfinishedSubmissionRow): ReactNode => {
  if (row.legacy) {
    const text = row.preview || row.subject || "—";
    const lines = expandDetailLines(text);
    return (
      <div className="space-y-1.5">
        {lines.map((line, index) => (
          <div key={index} className="block break-words border-l-2 border-muted-foreground/25 pl-2 text-[11px] leading-relaxed">
            {line.length > 0 ? line : "\u00a0"}
          </div>
        ))}
      </div>
    );
  }
  if (Array.isArray(row.data)) {
    return <div className="text-[11px]">{renderUnfinishedDetailValue(row.data)}</div>;
  }
  const data =
    row.data && typeof row.data === "object" && !Array.isArray(row.data)
      ? (row.data as Record<string, unknown>)
      : {};
  const entries = Object.entries(data);
  if (entries.length === 0) {
    return <div className="text-[11px] text-muted-foreground">—</div>;
  }
  return (
    <div className="space-y-3">
      {entries.map(([key, value]) => (
        <div key={key} className="block border-b border-border/50 pb-3 last:border-b-0 last:pb-0">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{key}</div>
          <div className="mt-1.5 block text-[11px]">{renderUnfinishedDetailValue(value)}</div>
        </div>
      ))}
    </div>
  );
};

const EmailLogsPanel = () => {
  const [logs, setLogs] = useState<EmailLogEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [unfinishedItems, setUnfinishedItems] = useState<UnfinishedSubmissionRow[]>([]);
  const [unfinishedExpandedUid, setUnfinishedExpandedUid] = useState<string | null>(null);
  const [expandedLogUids, setExpandedLogUids] = useState<Set<string>>(() => new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seenLogIdsRef = useRef<Set<string>>(new Set());
  const initialLoadRef = useRef(true);
  const lastFiltersRef = useRef<string | null>(null);
  const skipNextLoadRef = useRef(false);
  const [filters, setFilters] = useState<EmailLogsFilters>({
    status: "",
    service: "",
    category: "",
    to: "",
    subject: "",
    fromDate: "",
    toDate: "",
    orderBy: "createdAt",
    orderDirection: "desc"
  });

  const activeFiltersCount = useMemo(() => {
    return Object.entries(filters).filter(([key, value]) => {
      if (key === "orderBy" || key === "orderDirection") {
        return false;
      }
      return value !== undefined && value !== null && value !== "";
    }).length;
  }, [filters]);

  const describeLog = useCallback((log: EmailLogEntry) => {
    const recipient = log.to || (Array.isArray(log.toList) ? log.toList.join(", ") : "");
    const subject = log.subject || log.category || log.messageId || "No subject";
    if (recipient) {
      return `${subject} to ${recipient}`;
    }
    return subject;
  }, []);

  const loadLogs = useCallback(
    async (override?: Partial<EmailLogsFilters>, options?: { background?: boolean }) => {
      const background = options?.background === true;
      if (!background) {
        setIsLoading(true);
        setError(null);
      }
      try {
        const requestFilters: EmailLogsFilters =
          override !== undefined
            ? { ...override, limit: 200, offset: 0 }
            : { ...filters, limit: 200, offset: 0 };
        const { limit, offset, ...signatureFilters } = requestFilters;
        const nextFiltersSignature = JSON.stringify(signatureFilters);
        const filtersChanged =
          lastFiltersRef.current !== null && lastFiltersRef.current !== nextFiltersSignature;
        const shouldToast = !initialLoadRef.current && !filtersChanged;
        const response = await fetchEmailLogs(requestFilters);
        const nextLogs = response.logs;
        if (shouldToast) {
          const newLogs = nextLogs.filter((log) => log.uid && !seenLogIdsRef.current.has(log.uid));
          if (newLogs.length === 1) {
            toast.info(`New email log: ${describeLog(newLogs[0])}`);
          } else if (newLogs.length > 1) {
            toast.info(`${newLogs.length} new email log entries.`);
          }
        }
        seenLogIdsRef.current = new Set(nextLogs.map((log) => log.uid).filter(Boolean));
        lastFiltersRef.current = nextFiltersSignature;
        initialLoadRef.current = false;
        setLogs(nextLogs);
        setTotalCount(response.count);
        try {
          const un = await fetchUnfinishedSubmissions();
          setUnfinishedItems(un.items);
        } catch {
          if (!background) {
            setUnfinishedItems([]);
          }
        }
      } catch (err: any) {
        if (!background) {
          setLogs([]);
          setTotalCount(0);
          setError(err?.message || "Failed to load email logs");
        }
      } finally {
        if (!background) {
          setIsLoading(false);
        }
      }
    },
    [filters]
  );

  useEffect(() => {
    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false;
      return;
    }
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    const POLL_MS = 15000;
    const id = setInterval(() => {
      loadLogs(undefined, { background: true });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [loadLogs]);

  const toggleUnfinishedExpand = (uid: string) => {
    setUnfinishedExpandedUid((prev) => (prev === uid ? null : uid));
  };

  const toggleLogExpand = (uid: string) => {
    setExpandedLogUids((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  };

  const handleFilterChange = (key: keyof EmailLogsFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    loadLogs();
  };

  const resetFilters: EmailLogsFilters = {
    status: "",
    service: "",
    category: "",
    to: "",
    subject: "",
    fromDate: "",
    toDate: "",
    orderBy: "createdAt",
    orderDirection: "desc"
  };

  const handleEmptyLogs = async () => {
    const confirmed = window.confirm(
      "Empty all email logs? This cannot be undone."
    );
    if (!confirmed) return;
    setIsLoading(true);
    setError(null);
    try {
      await clearAllEmailLogs();
      try {
        const un = await fetchUnfinishedSubmissions();
        setUnfinishedItems(un.items);
      } catch {
        setUnfinishedItems([]);
      }
      skipNextLoadRef.current = true;
      setFilters(resetFilters);
      setLogs([]);
      setTotalCount(0);
      lastFiltersRef.current = JSON.stringify(resetFilters);
      seenLogIdsRef.current = new Set();
    } catch (err: any) {
      setError(err?.message || "Failed to clear email logs");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportGeneralLogs = useCallback(async () => {
    if (logs.length === 0) {
      toast.info("No logs to export.");
      return;
    }
    try {
      await exportEmailLogsToExcel(logs);
      toast.success("Excel file download started.");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Export failed.";
      toast.error(message);
    }
  }, [logs]);

  const handleExportUnfinished = useCallback(async () => {
    if (unfinishedItems.length === 0) {
      toast.info("No unfinished submissions to export.");
      return;
    }
    try {
      await exportUnfinishedSubmissionsToExcel(unfinishedItems);
      toast.success("Excel file download started.");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Export failed.";
      toast.error(message);
    }
  }, [unfinishedItems]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Logs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 bg-white">
          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full max-w-2xl grid-cols-3">
              <TabsTrigger value="general">General logs</TabsTrigger>
              <TabsTrigger value="unfinished">Unfinished submissions</TabsTrigger>
              <TabsTrigger value="live">Live</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-4 pt-2">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={handleApplyFilters}
                    disabled={isLoading}
                  >
                    {isLoading ? "Loading..." : "Refresh"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleEmptyLogs}
                    disabled={isLoading}
                  >
                    Empty logs
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleExportGeneralLogs}
                    disabled={isLoading || logs.length === 0}
                  >
                    Export to Excel
                  </Button>
                </div>
              </div>

              {error && <p className="text-sm text-red-500">{error}</p>}

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">Status</label>
                  <select
                    className="w-full rounded border px-3 py-2 text-sm"
                    value={filters.status || ""}
                    onChange={(event) => handleFilterChange("status", event.target.value)}
                  >
                    <option value="">All</option>
                    <option value="sent">Sent</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">Service</label>
                  <select
                    className="w-full rounded border px-3 py-2 text-sm"
                    value={filters.service || ""}
                    onChange={(event) => handleFilterChange("service", event.target.value)}
                  >
                    <option value="">All</option>
                    <option value="smtp">SMTP</option>
                    <option value="ses">Amazon SES</option>
                    <option value="mailjet">Mailjet</option>
                    <option value="resend">Resend</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">Category</label>
                  <Input
                    value={filters.category || ""}
                    onChange={(event) => handleFilterChange("category", event.target.value)}
                    placeholder="registration, login"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">Recipient</label>
                  <Input
                    value={filters.to || ""}
                    onChange={(event) => handleFilterChange("to", event.target.value)}
                    placeholder="name@company.com"
                  />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <label className="text-xs font-medium text-gray-600">Subject</label>
                  <Input
                    value={filters.subject || ""}
                    onChange={(event) => handleFilterChange("subject", event.target.value)}
                    placeholder="Subject contains..."
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">From date</label>
                  <Input
                    type="date"
                    value={filters.fromDate || ""}
                    onChange={(event) => handleFilterChange("fromDate", event.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">To date</label>
                  <Input
                    type="date"
                    value={filters.toDate || ""}
                    onChange={(event) => handleFilterChange("toDate", event.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                <span>{totalCount} log entries</span>
                {activeFiltersCount > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {activeFiltersCount} filter{activeFiltersCount === 1 ? "" : "s"} applied
                  </Badge>
                )}
              </div>

              <div className="rounded border overflow-hidden">
                <div className="grid grid-cols-[1.25rem_1.2fr_1.6fr_1.4fr_0.8fr_0.8fr_1fr_1fr] gap-3 p-3 text-xs font-medium text-gray-600 sticky top-0 z-10 bg-background border-b shrink-0">
                  <span className="w-5 shrink-0" aria-hidden />
                  <span>Time</span>
                  <span>Recipient</span>
                  <span>Subject</span>
                  <span>Status</span>
                  <span>Service</span>
                  <span>Category</span>
                  <span>Message ID</span>
                </div>
                <div className="divide-y max-h-[60vh] overflow-y-auto">
                  {isLoading && (
                    <div className="p-3 text-sm text-gray-500">Loading logs...</div>
                  )}
                  {!isLoading && logs.length === 0 && (
                    <div className="p-3 text-sm text-gray-500">No logs found.</div>
                  )}
                  {!isLoading &&
                    logs.map((log) => {
                      const expanded = expandedLogUids.has(log.uid);
                      const detailText =
                        log.status === "failed" ? log.errorMessage || "Failed" : log.preview || "—";
                      const detailLines =
                        typeof detailText === "string" ? expandDetailLines(detailText) : [String(detailText)];
                      return (
                        <div key={log.uid} className="text-xs text-gray-700">
                          <div
                            role="button"
                            tabIndex={0}
                            aria-expanded={expanded}
                            aria-label={expanded ? "Collapse log details" : "Expand log details"}
                            onClick={() => toggleLogExpand(log.uid)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                toggleLogExpand(log.uid);
                              }
                            }}
                            className={`grid w-full grid-cols-[1.25rem_1.2fr_1.6fr_1.4fr_0.8fr_0.8fr_1fr_1fr] gap-3 p-3 text-left cursor-pointer select-none hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${expanded ? "bg-muted/25" : ""
                              }`}
                          >
                            <span className="text-muted-foreground" aria-hidden>
                              {expanded ? "\u25be" : "\u25b8"}
                            </span>
                            <span>{formatDate(log.createdAt)}</span>
                            <span className="truncate">{log.to || "—"}</span>
                            <span className="truncate">{log.subject || "—"}</span>
                            <span>
                              <span
                                className={`inline-flex items-center rounded border px-2 py-0.5 ${statusBadgeClass(log.status)}`}
                              >
                                {log.status || "unknown"}
                              </span>
                            </span>
                            <span className="uppercase">{log.service || "—"}</span>
                            <span className="truncate">{log.category || "—"}</span>
                            <span className="truncate">{log.messageId || "—"}</span>
                          </div>
                          {expanded && (
                            <div className="border-t border-border/40 bg-muted/20 px-3 pb-3 pt-2 pl-10">
                              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                Details
                              </div>
                              <div className="mt-1.5 space-y-1">
                                {detailLines.map((line, index) => (
                                  <div key={index} className="block text-[11px] leading-relaxed text-gray-700">
                                    {line.length > 0 ? line : "\u00a0"}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="live" className="space-y-4 pt-2">
              <AdminLiveLogsPanel />
            </TabsContent>

            <TabsContent value="unfinished" className="space-y-4 pt-2">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <p className="text-sm text-muted-foreground sm:max-w-[42rem]">
                  In-progress public form payloads. New rows are stored until the user completes submission; legacy rows are
                  derived from historical form backup emails in the log.
                </p>
                <Button
                  variant="outline"
                  className="shrink-0"
                  onClick={handleExportUnfinished}
                  disabled={isLoading || unfinishedItems.length === 0}
                >
                  Export to Excel
                </Button>
              </div>
              <div className="rounded border overflow-hidden">
                <div className="grid grid-cols-[0.7fr_1.1fr_1fr_1.2fr_1fr] gap-3 p-3 text-xs font-medium text-gray-600 sticky top-0 z-10 bg-background border-b shrink-0">
                  <span>Legacy</span>
                  <span>Stage</span>
                  <span>Updated</span>
                  <span>Form / subject</span>
                  <span>Email</span>
                </div>
                <div className="divide-y max-h-[50vh] overflow-y-auto">
                  {isLoading && (
                    <div className="p-3 text-sm text-gray-500">Loading…</div>
                  )}
                  {!isLoading && unfinishedItems.length === 0 && (
                    <div className="p-3 text-sm text-gray-500">No unfinished submissions.</div>
                  )}
                  {!isLoading &&
                    unfinishedItems.map((row) => {
                      const expanded = unfinishedExpandedUid === row.uid;
                      return (
                        <div key={row.uid} className="text-xs text-gray-700">
                          <div
                            role="button"
                            tabIndex={0}
                            aria-expanded={expanded}
                            aria-label={expanded ? "Hide submission data" : "Show submission data"}
                            onClick={() => toggleUnfinishedExpand(row.uid)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                toggleUnfinishedExpand(row.uid);
                              }
                            }}
                            className={`grid w-full grid-cols-[0.7fr_1.1fr_1fr_1.2fr_1fr] gap-3 p-3 items-start text-left cursor-pointer select-none hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${expanded ? "bg-muted/25" : ""
                              }`}
                          >
                            <span>
                              {row.legacy ? (
                                <Badge variant="secondary" className="text-[10px]">
                                  Legacy
                                </Badge>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </span>
                            <span>{stageLabel(row)}</span>
                            <span>{formatDate(unfinishedDate(row) || undefined)}</span>
                            <span className="truncate" title={unfinishedPrimaryLabel(row)}>
                              {unfinishedPrimaryLabel(row)}
                            </span>
                            <span className="truncate">{row.email || row.to || "—"}</span>
                          </div>
                          {expanded && (
                            <div className="max-h-64 overflow-y-auto border-t border-dashed bg-muted/30 px-3 py-3 pl-4">
                              {renderUnfinishedDetailsBody(row)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailLogsPanel;
